import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { FolderOpen, FileText, Download, Trash2, History, Clock, RotateCcw, ExternalLink, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  contract: { label: 'Contract', class: 'bg-blue-100 text-blue-800' },
  instruction: { label: 'Instruction', class: 'bg-purple-100 text-purple-800' },
  source: { label: 'Source', class: 'bg-yellow-100 text-yellow-800' },
  deliverable: { label: 'Deliverable', class: 'bg-green-100 text-green-800' },
  invoice: { label: 'Invoice', class: 'bg-teal-100 text-teal-800' },
  compliance: { label: 'Compliance', class: 'bg-orange-100 text-orange-800' },
  other: { label: 'Other', class: 'bg-gray-100 text-gray-800' }
};

const Files = () => {
  const { getAuthHeaders } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Version history dialog state
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [versions, setVersions] = useState({ current: null, versions: [] });
  const [loadingVersions, setLoadingVersions] = useState(false);

  const fetchFiles = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const response = await fetch(`${API_URL}/api/files?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFiles(data);
      }
    } catch (error) {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [categoryFilter]);

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file? All version history will be lost.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/files/${fileId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('File deleted');
        fetchFiles();
      }
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const fetchVersionHistory = async (file) => {
    setSelectedFile(file);
    setLoadingVersions(true);
    setVersionDialogOpen(true);
    
    try {
      const response = await fetch(`${API_URL}/api/files/${file.file_id}/versions`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVersions(data);
      }
    } catch (error) {
      toast.error('Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRollback = async (versionNumber) => {
    if (!window.confirm(`Are you sure you want to rollback to version ${versionNumber}? This will create a new version.`)) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/files/${selectedFile.file_id}/rollback/${versionNumber}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include'
        }
      );
      if (response.ok) {
        toast.success(`Rolled back to version ${versionNumber}`);
        fetchVersionHistory(selectedFile);
        fetchFiles();
      }
    } catch (error) {
      toast.error('Failed to rollback');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const getCategoryBadge = (category) => {
    const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  // Count files with versions
  const versionedFilesCount = files.filter(f => f.current_version > 1).length;

  return (
    <div className="space-y-6" data-testid="files-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen className="h-6 w-6 text-blue-600" />
            File Manager
          </h1>
          <p className="text-muted-foreground">
            {files.length} files • {versionedFilesCount} with version history
          </p>
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]" data-testid="file-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No files found</h3>
              <p className="text-muted-foreground">Files will appear here when uploaded to work orders or entities</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.file_id} data-testid={`file-row-${file.file_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{file.filename}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getCategoryBadge(file.category)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {file.entity_type}: {file.entity_id}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge 
                          variant="secondary" 
                          className={file.current_version > 1 ? 'bg-blue-100 text-blue-800' : ''}
                        >
                          v{file.current_version || 1}
                        </Badge>
                        {file.current_version > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => fetchVersionHistory(file)}
                            title="View version history"
                            data-testid={`version-history-${file.file_id}`}
                          >
                            <History className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatSize(file.file_size)}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(file.uploaded_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {file.current_version > 1 && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => fetchVersionHistory(file)}
                            title="Version history"
                          >
                            <History className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                        {file.file_url && (
                          <Button variant="ghost" size="icon" asChild title="Download/View">
                            <a href={file.file_url} target="_blank" rel="noopener noreferrer">
                              <Download className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(file.file_id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Version History Dialog */}
      <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Version History
            </DialogTitle>
          </DialogHeader>
          
          {loadingVersions ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* File Info */}
              {selectedFile && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-5 w-5 text-gray-600" />
                    <span className="font-semibold">{selectedFile.filename}</span>
                    <Badge variant="secondary">Current: v{selectedFile.current_version || 1}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {selectedFile.entity_type}: {selectedFile.entity_id}
                  </p>
                </div>
              )}

              {/* Current Version */}
              {versions.versions && versions.versions.length > 0 && versions.versions.find(v => v.is_current) && (
                <Card className="border-green-200 bg-green-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-green-800 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Current Version (v{versions.versions.find(v => v.is_current)?.version_number})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {(() => {
                      const currentVer = versions.versions.find(v => v.is_current);
                      return (
                        <div className="flex items-center justify-between">
                          <div className="text-sm space-y-1">
                            <p><span className="text-muted-foreground">Size:</span> {formatSize(currentVer?.file_size)}</p>
                            <p><span className="text-muted-foreground">Uploaded:</span> {formatDateTime(currentVer?.uploaded_at)}</p>
                            {currentVer?.notes && (
                              <p><span className="text-muted-foreground">Notes:</span> {currentVer.notes}</p>
                            )}
                          </div>
                          <Button variant="outline" size="sm" asChild>
                            <a href={currentVer?.file_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4 mr-1" />
                              View
                            </a>
                          </Button>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Previous Versions */}
              {versions.versions && versions.versions.filter(v => !v.is_current).length > 0 ? (
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Previous Versions
                  </h4>
                  {versions.versions.filter(v => !v.is_current).map((version) => (
                    <Card key={version.version_id} className="border-gray-200">
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">v{version.version_number}</Badge>
                              <span className="text-sm text-muted-foreground">
                                {formatDateTime(version.uploaded_at)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {formatSize(version.file_size)}
                              {version.notes && ` • ${version.notes}`}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {version.file_url && (
                              <Button variant="outline" size="sm" asChild>
                                <a href={version.file_url} target="_blank" rel="noopener noreferrer">
                                  <Download className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleRollback(version.version_number)}
                              data-testid={`rollback-${version.version_number}`}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Restore
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <p>No previous versions available</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Files;
